/**
 * Keeps one cart line per variant when a product is added from a Rebuy widget and from the theme.
 *
 * Rebuy tags every product added from one of its widgets with attribution properties
 * (_source, _widget_id, _attribution). Shopify treats lines with different properties as different
 * items, so the same variant added from a Rebuy widget (homepage carousels, Smart Cart
 * recommendations) and from a product form (PDP, product cards, search) ends up split in two lines
 * instead of adding up. Every time Rebuy reads the cart, this folds those lines back into the line
 * that was in the cart first, with the summed quantity.
 */
(() => {
  // Properties Rebuy adds to its lines only to attribute the sale.
  const REBUY_ATTRIBUTION_PROPERTIES = ['_source', '_widget_id', '_attribution', '_r_tab_index', '_r_tab_title'];
  // Rebuy lines with their own pricing or grouping (gifts, bundles, tiers, subscriptions) stay apart.
  const SEPARATE_LINE_ATTRIBUTION = /gift|bundle|buy more save more|subscription/i;

  let merging = false;
  let recheckAfterMerge = false;
  let lastUpdates = null;

  const getLineMergeKey = (item) => {
    const properties = { ...(item.properties || {}) };

    if (properties._source === 'Rebuy') {
      if (SEPARATE_LINE_ATTRIBUTION.test(properties._attribution || '')) return null;
      REBUY_ATTRIBUTION_PROPERTIES.forEach((name) => delete properties[name]);
    }

    const sellingPlanId = item.selling_plan_allocation?.selling_plan?.id || null;
    const sortedProperties = Object.keys(properties)
      .sort()
      .map((name) => [name, properties[name]]);

    return JSON.stringify([item.variant_id, sellingPlanId, sortedProperties]);
  };

  // Returns the /cart/update.js `updates` that fold duplicate lines together, or null when there are none.
  const getMergeUpdates = (cart) => {
    const linesByMergeKey = new Map();
    cart.items.forEach((item) => {
      const mergeKey = getLineMergeKey(item);
      if (!mergeKey) return;
      if (!linesByMergeKey.has(mergeKey)) linesByMergeKey.set(mergeKey, []);
      linesByMergeKey.get(mergeKey).push(item);
    });

    const updates = {};
    linesByMergeKey.forEach((lines) => {
      // A line split by a discount repeats its key: that is still one line.
      const lineKeys = [...new Set(lines.map((line) => line.key))];
      if (lineKeys.length < 2) return;

      // Shopify lists the newest line first, so the last key is the line that was in the cart first.
      const keptKey = lineKeys[lineKeys.length - 1];
      lineKeys.forEach((key) => (updates[key] = 0));
      updates[keptKey] = lines.reduce((total, line) => total + line.quantity, 0);
    });

    return Object.keys(updates).length ? updates : null;
  };

  const mergeDuplicateLines = async (cart) => {
    const updates = cart && Array.isArray(cart.items) ? getMergeUpdates(cart) : null;
    if (!updates) {
      lastUpdates = null;
      return;
    }

    if (merging) {
      recheckAfterMerge = true;
      return;
    }

    // The same merge again means the cart did not take the last one: stop instead of looping.
    const signature = JSON.stringify(updates);
    if (signature === lastUpdates) return;
    lastUpdates = signature;

    merging = true;
    try {
      const sections = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
      );

      const response = await fetch(FoxTheme.routes.cart_update_url, {
        ...FoxTheme.utils.fetchConfig(),
        body: JSON.stringify({ updates, sections, sections_url: window.location.pathname }),
      });
      const mergedCart = await response.json();
      if (!response.ok || mergedCart.status) {
        throw new Error(mergedCart.description || mergedCart.message || response.statusText);
      }

      if (!getMergeUpdates(mergedCart)) lastUpdates = null;
      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: mergedCart });
    } catch (error) {
      console.error('Could not merge duplicate cart lines.', error);
    } finally {
      merging = false;
    }

    if (recheckAfterMerge) {
      recheckAfterMerge = false;
      const response = await fetch(`${FoxTheme.routes.cart_url}.js`, FoxTheme.utils.fetchConfig('json', 'GET'));
      mergeDuplicateLines(await response.json());
    }
  };

  // Rebuy reads the cart again after every cart request on the page (theme product forms, its own
  // widgets, Smart Cart) and announces it here. detail.cart is Rebuy's cart module; its `cart` is
  // the Shopify cart JSON.
  document.addEventListener('rebuy:cart.change', (event) => {
    mergeDuplicateLines(event.detail?.cart?.cart).catch((error) => {
      console.error('Could not merge duplicate cart lines.', error);
    });
  });
})();
